import type { ReactNode } from 'react'
import { useSettingsStore } from '@/stores/settingsStore'

interface AuthBackgroundProps {
  children: ReactNode
}

export function AuthBackground({ children }: AuthBackgroundProps) {
  const resolvedTheme = useSettingsStore((s) => s.resolvedTheme)
  const d = resolvedTheme === 'dark'

  const vi = (a: number) => d ? `rgba(99,102,241,${a})` : `rgba(67,56,202,${Math.min(a * 3.2, 0.98)})`
  const vc = (a: number) => d ? `rgba(6,182,212,${a})`  : `rgba(8,145,178,${Math.min(a * 3.2, 0.98)})`
  const vp = (a: number) => d ? `rgba(124,58,237,${a})` : `rgba(109,40,217,${Math.min(a * 3.2, 0.98)})`
  const it = d ? 'rgba(165,180,252,0.88)' : 'rgba(49,46,129,0.94)'
  const ct = d ? 'rgba(103,232,249,0.88)' : 'rgba(14,116,144,0.94)'
  const pt = d ? 'rgba(196,181,253,0.88)' : 'rgba(76,29,149,0.94)'

  return (
    <div
      style={{
        minHeight: '100vh',
        background: d
          ? '#020617'
          : 'linear-gradient(135deg, #C3CEE8 0%, #D0DAEA 35%, #C8D6EE 65%, #C0CCEC 100%)',
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {/* ── Decorative layer ── */}
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden' }}>

        {/* Blob 1 */}
        <div className="auth-blob-1" style={{
          position: 'absolute', top: '-18%', right: '-8%',
          width: '62vw', height: '62vw', borderRadius: '50%',
          background: d
            ? 'radial-gradient(circle, rgba(124,58,237,0.26) 0%, rgba(37,99,235,0.15) 40%, transparent 70%)'
            : 'radial-gradient(circle, rgba(124,58,237,0.42) 0%, rgba(37,99,235,0.24) 45%, transparent 72%)',
          filter: 'blur(64px)',
        }} />

        {/* Blob 2 */}
        <div className="auth-blob-2" style={{
          position: 'absolute', bottom: '-22%', left: '-12%',
          width: '68vw', height: '68vw', borderRadius: '50%',
          background: d
            ? 'radial-gradient(circle, rgba(6,182,212,0.22) 0%, rgba(37,99,235,0.11) 40%, transparent 70%)'
            : 'radial-gradient(circle, rgba(6,182,212,0.38) 0%, rgba(37,99,235,0.20) 45%, transparent 72%)',
          filter: 'blur(80px)',
        }} />

        {/* Blob 3 */}
        <div className="auth-blob-3" style={{
          position: 'absolute', top: '-5%', left: '-15%',
          width: '40vw', height: '40vw', borderRadius: '50%',
          background: d
            ? 'radial-gradient(circle, rgba(37,99,235,0.16) 0%, transparent 65%)'
            : 'radial-gradient(circle, rgba(37,99,235,0.30) 0%, transparent 65%)',
          filter: 'blur(50px)',
        }} />

        {/* Center glow */}
        <div className="auth-center-glow" style={{
          position: 'absolute', top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '50vw', height: '50vw', borderRadius: '50%',
          background: d
            ? 'radial-gradient(circle, rgba(99,102,241,0.13) 0%, transparent 62%)'
            : 'radial-gradient(circle, rgba(99,102,241,0.22) 0%, transparent 62%)',
          filter: 'blur(40px)',
        }} />

        {/* Pulse rings behind the card */}
        <div style={{
          position: 'absolute', inset: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div className="auth-ring-1" style={{
            position: 'absolute',
            width: 520, height: 520, borderRadius: '50%', flexShrink: 0,
            border: d
              ? '1px solid rgba(99,102,241,0.18)'
              : '1px solid rgba(99,102,241,0.28)',
          }} />
          <div className="auth-ring-2" style={{
            position: 'absolute',
            width: 520, height: 520, borderRadius: '50%', flexShrink: 0,
            border: d
              ? '1px solid rgba(6,182,212,0.12)'
              : '1px solid rgba(6,182,212,0.22)',
          }} />
          <div className="auth-ring-3" style={{
            position: 'absolute',
            width: 680, height: 680, borderRadius: '50%', flexShrink: 0,
            border: d
              ? '1px solid rgba(124,58,237,0.09)'
              : '1px solid rgba(124,58,237,0.16)',
          }} />
        </div>

        {/* Dot grid */}
        <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
          <defs>
            <pattern id="auth-dots" x="0" y="0" width="32" height="32" patternUnits="userSpaceOnUse">
              <circle cx="0" cy="0" r="0.9"
                fill={d ? 'rgba(148,163,184,0.14)' : 'rgba(55,65,120,0.2)'} />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#auth-dots)" />
        </svg>

        {/* Vignette overlay for depth */}
        <div style={{
          position: 'absolute', inset: 0,
          background: d
            ? 'radial-gradient(ellipse at center, transparent 38%, rgba(2,6,23,0.55) 100%)'
            : 'radial-gradient(ellipse at center, transparent 40%, rgba(148,163,210,0.35) 100%)',
        }} />

        {/* ══════════════════════════════════════════════
            TOP-LEFT ERD cluster
        ══════════════════════════════════════════════ */}
        <svg
          style={{ position: 'absolute', top: 0, left: 0, width: '56%', height: '68%' }}
          viewBox="0 0 570 460"
          preserveAspectRatio="xMinYMin meet"
          overflow="visible"
        >
          <defs>
            <filter id="auth-ghost-tl" x="-30%" y="-30%" width="160%" height="160%">
              <feGaussianBlur stdDeviation="3.5" />
            </filter>
            <filter id="auth-glow-tl" x="-150%" y="-150%" width="400%" height="400%">
              <feGaussianBlur stdDeviation="3.5" />
            </filter>
          </defs>

          {/* ── Glow layers for solid connections ── */}
          <line x1="220" y1="106" x2="220" y2="185"
            stroke={vc(0.5)} strokeWidth="6"
            filter="url(#auth-glow-tl)"
            className="auth-solid-pulse-1"
          />
          <line x1="130" y1="201" x2="130" y2="245"
            stroke={vc(0.4)} strokeWidth="5"
            filter="url(#auth-glow-tl)"
            className="auth-solid-pulse-2"
          />
          <line x1="55" y1="108" x2="55" y2="230"
            stroke={vi(0.3)} strokeWidth="4"
            filter="url(#auth-glow-tl)"
            className="auth-solid-pulse-3"
          />

          {/* ── Connection lines ── */}
          {/* users → diagrams */}
          <line x1="100" y1="80" x2="175" y2="80" stroke={vi(0.32)} strokeWidth="1" strokeDasharray="5,4" className="auth-line-flow-1" />
          {/* diagrams → permissions */}
          <line x1="265" y1="80" x2="325" y2="80" stroke={vi(0.22)} strokeWidth="1" strokeDasharray="4,5" className="auth-line-flow-2" />
          {/* permissions → roles (vertical stub) */}
          <line x1="370" y1="106" x2="370" y2="158" stroke={vp(0.2)} strokeWidth="1" strokeDasharray="3,6" className="auth-line-flow-3" />
          {/* diagrams → projects (vertical) */}
          <line x1="220" y1="106" x2="220" y2="185" stroke={vc(0.28)} strokeWidth="1" />
          {/* projects → workspaces elbow */}
          <line x1="175" y1="201" x2="130" y2="201" stroke={vc(0.22)} strokeWidth="1" strokeDasharray="5,4" className="auth-line-flow-4" />
          <line x1="130" y1="201" x2="130" y2="245" stroke={vc(0.18)} strokeWidth="1" />
          <line x1="100" y1="245" x2="130" y2="245" stroke={vc(0.18)} strokeWidth="1" strokeDasharray="3,5" className="auth-line-flow-5" />
          {/* users → workspaces (vertical) */}
          <line x1="55" y1="108" x2="55" y2="230" stroke={vi(0.16)} strokeWidth="1" strokeDasharray="3,7" className="auth-line-flow-6" />
          {/* workspaces → ghost workspace_members */}
          <line x1="55" y1="268" x2="55" y2="320" stroke={vp(0.14)} strokeWidth="1" strokeDasharray="2,7" className="auth-line-flow-7" />
          <line x1="55" y1="320" x2="130" y2="320" stroke={vp(0.12)} strokeWidth="1" strokeDasharray="2,7" className="auth-line-flow-8" />
          <line x1="130" y1="320" x2="130" y2="348" stroke={vp(0.12)} strokeWidth="1" strokeDasharray="2,7" className="auth-line-flow-1" />

          {/* ── Terminators ── */}
          <line x1="96"  y1="75"  x2="96"  y2="85"  stroke={vi(0.52)} strokeWidth="1.2" />
          <line x1="179" y1="75"  x2="179" y2="85"  stroke={vi(0.52)} strokeWidth="1.2" />
          <line x1="261" y1="75"  x2="261" y2="85"  stroke={vi(0.42)} strokeWidth="1.2" />
          <line x1="329" y1="75"  x2="329" y2="85"  stroke={vp(0.4)}  strokeWidth="1.2" />
          <line x1="216" y1="181" x2="216" y2="189" stroke={vc(0.46)} strokeWidth="1.2" />
          <line x1="171" y1="197" x2="171" y2="205" stroke={vc(0.42)} strokeWidth="1.2" />
          <line x1="96"  y1="241" x2="96"  y2="249" stroke={vc(0.38)} strokeWidth="1.2" />

          {/* ── Nodes ── */}
          <circle cx="100" cy="80"  r="4.5" fill={vi(0.75)} />
          <circle cx="175" cy="80"  r="4.5" fill={vi(0.65)} />
          <circle cx="265" cy="80"  r="4"   fill={vc(0.65)} />
          <circle cx="325" cy="80"  r="4"   fill={vp(0.6)}  />
          <circle cx="220" cy="106" r="3.5" fill={vc(0.6)}  />
          <circle cx="220" cy="185" r="4.5" fill={vc(0.65)} />
          <circle cx="175" cy="201" r="4"   fill={vc(0.55)} />
          <circle cx="130" cy="245" r="3.5" fill={vc(0.5)}  />
          <circle cx="100" cy="245" r="4"   fill={vc(0.6)}  />
          <circle cx="55"  cy="108" r="3"   fill={vi(0.5)}  />
          <circle cx="55"  cy="230" r="3"   fill={vp(0.45)} />
          <circle cx="370" cy="106" r="3"   fill={vp(0.5)}  />

          {/* ── Traveling dots ── */}
          {/* users → diagrams */}
          <circle r="2" fill={vi(0.95)}>
            <animateMotion dur="2.5s" repeatCount="indefinite" path="M100,80 L175,80" />
            <animate attributeName="opacity" values="0;1;1;0" keyTimes="0;0.08;0.92;1" dur="2.5s" repeatCount="indefinite" />
          </circle>
          <circle r="2" fill={vi(0.95)}>
            <animateMotion dur="2.5s" repeatCount="indefinite" begin="1.25s" path="M100,80 L175,80" />
            <animate attributeName="opacity" values="0;1;1;0" keyTimes="0;0.08;0.92;1" dur="2.5s" repeatCount="indefinite" begin="1.25s" />
          </circle>

          {/* diagrams → projects */}
          <circle r="2" fill={vc(0.95)}>
            <animateMotion dur="2s" repeatCount="indefinite" path="M220,106 L220,185" />
            <animate attributeName="opacity" values="0;1;1;0" keyTimes="0;0.08;0.92;1" dur="2s" repeatCount="indefinite" />
          </circle>
          <circle r="2" fill={vc(0.95)}>
            <animateMotion dur="2s" repeatCount="indefinite" begin="1s" path="M220,106 L220,185" />
            <animate attributeName="opacity" values="0;1;1;0" keyTimes="0;0.08;0.92;1" dur="2s" repeatCount="indefinite" begin="1s" />
          </circle>

          {/* projects → workspaces elbow */}
          <circle r="1.8" fill={vc(0.9)}>
            <animateMotion dur="3.2s" repeatCount="indefinite" path="M175,201 L130,201 L130,245 L100,245" />
            <animate attributeName="opacity" values="0;1;1;0" keyTimes="0;0.06;0.94;1" dur="3.2s" repeatCount="indefinite" />
          </circle>

          {/* diagrams → permissions */}
          <circle r="1.8" fill={vi(0.9)}>
            <animateMotion dur="2.2s" repeatCount="indefinite" begin="0.4s" path="M265,80 L325,80" />
            <animate attributeName="opacity" values="0;1;1;0" keyTimes="0;0.08;0.92;1" dur="2.2s" repeatCount="indefinite" begin="0.4s" />
          </circle>

          {/* ── Tables ── */}
          <rect x="8"   y="62" width="92" height="16" rx="3" fill={vi(0.22)} stroke={vi(0.38)} strokeWidth="1" />
          <text x="16"  y="73.5" fontSize="7.5" fill={it} fontFamily="monospace">users</text>
          <rect x="8"   y="78" width="92" height="10" fill={vi(0.07)} stroke={vi(0.14)} strokeWidth="0.5" />
          <rect x="12"  y="80.5" width="28" height="5" rx="1" fill={vi(0.22)} />
          <rect x="8"   y="88" width="92" height="10" fill={vi(0.05)} stroke={vi(0.1)}  strokeWidth="0.5" />
          <rect x="12"  y="90.5" width="48" height="5" rx="1" fill={vi(0.14)} />
          <rect x="8"   y="98" width="92" height="10" fill={vi(0.05)} stroke={vi(0.1)}  strokeWidth="0.5" />
          <rect x="12"  y="100.5" width="38" height="5" rx="1" fill={vi(0.14)} />

          <rect x="175" y="62" width="90" height="16" rx="3" fill={vc(0.2)}  stroke={vc(0.32)} strokeWidth="1" />
          <text x="183" y="73.5" fontSize="7.5" fill={ct} fontFamily="monospace">diagrams</text>
          <rect x="175" y="78" width="90" height="10" fill={vc(0.06)} stroke={vc(0.12)} strokeWidth="0.5" />
          <rect x="179" y="80.5" width="35" height="5" rx="1" fill={vc(0.18)} />
          <rect x="175" y="88" width="90" height="10" fill={vc(0.04)} stroke={vc(0.09)} strokeWidth="0.5" />
          <rect x="179" y="90.5" width="52" height="5" rx="1" fill={vc(0.12)} />
          <rect x="175" y="98" width="90" height="10" fill={vc(0.04)} stroke={vc(0.09)} strokeWidth="0.5" />
          <rect x="179" y="100.5" width="40" height="5" rx="1" fill={vc(0.12)} />
          <rect x="175" y="108" width="90" height="10" fill={vc(0.04)} stroke={vc(0.07)} strokeWidth="0.5" />
          <rect x="179" y="110.5" width="30" height="5" rx="1" fill={vc(0.1)}  />

          <rect x="325" y="62" width="90" height="16" rx="3" fill={vp(0.18)} stroke={vp(0.3)}  strokeWidth="1" />
          <text x="333" y="73.5" fontSize="7.5" fill={pt} fontFamily="monospace">permissions</text>
          <rect x="325" y="78" width="90" height="10" fill={vp(0.06)} stroke={vp(0.11)} strokeWidth="0.5" />
          <rect x="329" y="80.5" width="30" height="5" rx="1" fill={vp(0.16)} />
          <rect x="325" y="88" width="90" height="10" fill={vp(0.04)} stroke={vp(0.08)} strokeWidth="0.5" />
          <rect x="329" y="90.5" width="50" height="5" rx="1" fill={vp(0.11)} />
          <rect x="325" y="98" width="90" height="10" fill={vp(0.04)} stroke={vp(0.08)} strokeWidth="0.5" />
          <rect x="329" y="100.5" width="38" height="5" rx="1" fill={vp(0.11)} />

          <rect x="175" y="185" width="90" height="16" rx="3" fill={vc(0.19)} stroke={vc(0.3)}  strokeWidth="1" />
          <text x="183" y="196.5" fontSize="7.5" fill={ct} fontFamily="monospace">projects</text>
          <rect x="175" y="201" width="90" height="10" fill={vc(0.06)} stroke={vc(0.11)} strokeWidth="0.5" />
          <rect x="179" y="203.5" width="40" height="5" rx="1" fill={vc(0.17)} />
          <rect x="175" y="211" width="90" height="10" fill={vc(0.04)} stroke={vc(0.08)} strokeWidth="0.5" />
          <rect x="179" y="213.5" width="55" height="5" rx="1" fill={vc(0.11)} />
          <rect x="175" y="221" width="90" height="10" fill={vc(0.04)} stroke={vc(0.08)} strokeWidth="0.5" />
          <rect x="179" y="223.5" width="33" height="5" rx="1" fill={vc(0.11)} />

          <rect x="8"   y="230" width="92" height="16" rx="3" fill={vp(0.17)} stroke={vp(0.28)} strokeWidth="1" />
          <text x="16"  y="241.5" fontSize="7.5" fill={pt} fontFamily="monospace">workspaces</text>
          <rect x="8"   y="246" width="92" height="10" fill={vp(0.05)} stroke={vp(0.1)}  strokeWidth="0.5" />
          <rect x="12"  y="248.5" width="32" height="5" rx="1" fill={vp(0.15)} />
          <rect x="8"   y="256" width="92" height="10" fill={vp(0.04)} stroke={vp(0.08)} strokeWidth="0.5" />
          <rect x="12"  y="258.5" width="54" height="5" rx="1" fill={vp(0.1)}  />

          <rect x="325" y="158" width="90" height="16" rx="3" fill={vp(0.13)} stroke={vp(0.22)} strokeWidth="1" />
          <text x="333" y="169.5" fontSize="7.5" fill={pt} fontFamily="monospace">roles</text>
          <rect x="325" y="174" width="90" height="10" fill={vp(0.04)} stroke={vp(0.08)} strokeWidth="0.5" />
          <rect x="329" y="176.5" width="44" height="5" rx="1" fill={vp(0.12)} />
          <rect x="325" y="184" width="90" height="10" fill={vp(0.04)} stroke={vp(0.07)} strokeWidth="0.5" />
          <rect x="329" y="186.5" width="30" height="5" rx="1" fill={vp(0.09)} />

          {/* Ghost: workspace_members */}
          <g filter="url(#auth-ghost-tl)" opacity={d ? 0.38 : 0.55}>
            <rect x="82"  y="348" width="100" height="16" rx="3" fill={vp(0.16)} stroke={vp(0.26)} strokeWidth="1" />
            <text x="90"  y="359.5" fontSize="7.5" fill={pt} fontFamily="monospace">workspace_members</text>
            <rect x="82"  y="364" width="100" height="10" fill={vp(0.06)} stroke={vp(0.1)} strokeWidth="0.5" />
            <rect x="86"  y="366.5" width="38" height="5" rx="1" fill={vp(0.16)} />
            <rect x="82"  y="374" width="100" height="10" fill={vp(0.04)} stroke={vp(0.08)} strokeWidth="0.5" />
            <rect x="86"  y="376.5" width="55" height="5" rx="1" fill={vp(0.1)}  />
            <rect x="82"  y="384" width="100" height="10" fill={vp(0.04)} stroke={vp(0.07)} strokeWidth="0.5" />
            <rect x="86"  y="386.5" width="40" height="5" rx="1" fill={vp(0.1)}  />
          </g>
        </svg>

        {/* ══════════════════════════════════════════════
            BOTTOM-RIGHT ERD cluster
        ══════════════════════════════════════════════ */}
        <svg
          style={{ position: 'absolute', bottom: 0, right: 0, width: '56%', height: '68%' }}
          viewBox="0 0 570 460"
          preserveAspectRatio="xMaxYMax meet"
          overflow="visible"
        >
          <defs>
            <filter id="auth-ghost-br" x="-30%" y="-30%" width="160%" height="160%">
              <feGaussianBlur stdDeviation="3.5" />
            </filter>
            <filter id="auth-glow-br" x="-150%" y="-150%" width="400%" height="400%">
              <feGaussianBlur stdDeviation="3.5" />
            </filter>
          </defs>

          {/* ── Glow layers for solid connections ── */}
          <line x1="350" y1="290" x2="350" y2="178"
            stroke={vi(0.45)} strokeWidth="6"
            filter="url(#auth-glow-br)"
            className="auth-solid-pulse-2"
          />
          <line x1="115" y1="259" x2="115" y2="185"
            stroke={vc(0.4)} strokeWidth="5"
            filter="url(#auth-glow-br)"
            className="auth-solid-pulse-1"
          />

          {/* ── Connection lines ── */}
          {/* tables → indexes (vertical) */}
          <line x1="350" y1="290" x2="350" y2="178" stroke={vi(0.26)} strokeWidth="1" />
          {/* tables → relations (horizontal) */}
          <line x1="255" y1="275" x2="160" y2="275" stroke={vi(0.28)} strokeWidth="1" strokeDasharray="5,4" className="auth-line-flow-2" />
          {/* relations → columns elbow */}
          <line x1="115" y1="259" x2="115" y2="185" stroke={vc(0.24)} strokeWidth="1" />
          <line x1="115" y1="185" x2="155" y2="185" stroke={vc(0.24)} strokeWidth="1" strokeDasharray="5,4" className="auth-line-flow-3" />
          {/* columns → constraints */}
          <line x1="245" y1="170" x2="300" y2="170" stroke={vc(0.22)} strokeWidth="1" strokeDasharray="4,5" className="auth-line-flow-4" />
          {/* indexes → schemas (vertical stub) */}
          <line x1="395" y1="162" x2="395" y2="110" stroke={vp(0.18)} strokeWidth="1" strokeDasharray="3,6" className="auth-line-flow-5" />
          {/* tables → ghost foreign_keys */}
          <line x1="300" y1="310" x2="300" y2="358" stroke={vc(0.15)} strokeWidth="1" strokeDasharray="2,7" className="auth-line-flow-6" />
          <line x1="300" y1="358" x2="200" y2="358" stroke={vc(0.12)} strokeWidth="1" strokeDasharray="2,7" className="auth-line-flow-7" />
          <line x1="200" y1="358" x2="200" y2="385" stroke={vc(0.12)} strokeWidth="1" strokeDasharray="2,7" className="auth-line-flow-8" />

          {/* ── Terminators ── */}
          <line x1="346" y1="285" x2="346" y2="295" stroke={vi(0.5)}  strokeWidth="1.2" />
          <line x1="354" y1="182" x2="354" y2="174" stroke={vi(0.5)}  strokeWidth="1.2" />
          <line x1="259" y1="270" x2="259" y2="280" stroke={vi(0.46)} strokeWidth="1.2" />
          <line x1="156" y1="270" x2="156" y2="280" stroke={vi(0.42)} strokeWidth="1.2" />
          <line x1="111" y1="181" x2="111" y2="189" stroke={vc(0.44)} strokeWidth="1.2" />
          <line x1="159" y1="181" x2="159" y2="189" stroke={vc(0.42)} strokeWidth="1.2" />
          <line x1="304" y1="165" x2="304" y2="175" stroke={vc(0.38)} strokeWidth="1.2" />

          {/* ── Nodes ── */}
          <circle cx="350" cy="290" r="4.5" fill={vi(0.75)} />
          <circle cx="350" cy="178" r="4.5" fill={vi(0.65)} />
          <circle cx="255" cy="275" r="4"   fill={vi(0.6)}  />
          <circle cx="160" cy="275" r="4.5" fill={vi(0.65)} />
          <circle cx="115" cy="259" r="4"   fill={vc(0.6)}  />
          <circle cx="115" cy="185" r="3.5" fill={vc(0.55)} />
          <circle cx="155" cy="185" r="4"   fill={vc(0.65)} />
          <circle cx="245" cy="170" r="3.5" fill={vc(0.55)} />
          <circle cx="300" cy="170" r="4"   fill={vc(0.6)}  />
          <circle cx="395" cy="162" r="3"   fill={vp(0.5)}  />
          <circle cx="395" cy="110" r="3"   fill={vp(0.42)} />

          {/* ── Traveling dots ── */}
          {/* tables → indexes (going up) */}
          <circle r="2" fill={vi(0.95)}>
            <animateMotion dur="2.2s" repeatCount="indefinite" path="M350,290 L350,178" />
            <animate attributeName="opacity" values="0;1;1;0" keyTimes="0;0.08;0.92;1" dur="2.2s" repeatCount="indefinite" />
          </circle>
          <circle r="2" fill={vi(0.95)}>
            <animateMotion dur="2.2s" repeatCount="indefinite" begin="1.1s" path="M350,290 L350,178" />
            <animate attributeName="opacity" values="0;1;1;0" keyTimes="0;0.08;0.92;1" dur="2.2s" repeatCount="indefinite" begin="1.1s" />
          </circle>

          {/* tables → relations */}
          <circle r="2" fill={vi(0.9)}>
            <animateMotion dur="2.8s" repeatCount="indefinite" path="M255,275 L160,275" />
            <animate attributeName="opacity" values="0;1;1;0" keyTimes="0;0.08;0.92;1" dur="2.8s" repeatCount="indefinite" />
          </circle>
          <circle r="2" fill={vi(0.9)}>
            <animateMotion dur="2.8s" repeatCount="indefinite" begin="1.4s" path="M255,275 L160,275" />
            <animate attributeName="opacity" values="0;1;1;0" keyTimes="0;0.08;0.92;1" dur="2.8s" repeatCount="indefinite" begin="1.4s" />
          </circle>

          {/* relations → columns vertical */}
          <circle r="1.8" fill={vc(0.9)}>
            <animateMotion dur="2s" repeatCount="indefinite" begin="0.6s" path="M115,259 L115,185" />
            <animate attributeName="opacity" values="0;1;1;0" keyTimes="0;0.08;0.92;1" dur="2s" repeatCount="indefinite" begin="0.6s" />
          </circle>

          {/* columns → constraints */}
          <circle r="1.8" fill={vc(0.95)}>
            <animateMotion dur="1.8s" repeatCount="indefinite" path="M245,170 L300,170" />
            <animate attributeName="opacity" values="0;1;1;0" keyTimes="0;0.08;0.92;1" dur="1.8s" repeatCount="indefinite" />
          </circle>

          {/* ── Tables ── */}
          <rect x="255" y="257" width="92" height="16" rx="3" fill={vi(0.22)} stroke={vi(0.36)} strokeWidth="1" />
          <text x="263" y="268.5" fontSize="7.5" fill={it} fontFamily="monospace">tables</text>
          <rect x="255" y="273" width="92" height="10" fill={vi(0.07)} stroke={vi(0.14)} strokeWidth="0.5" />
          <rect x="259" y="275.5" width="40" height="5" rx="1" fill={vi(0.2)}  />
          <rect x="255" y="283" width="92" height="10" fill={vi(0.05)} stroke={vi(0.1)}  strokeWidth="0.5" />
          <rect x="259" y="285.5" width="55" height="5" rx="1" fill={vi(0.13)} />
          <rect x="255" y="293" width="92" height="10" fill={vi(0.05)} stroke={vi(0.1)}  strokeWidth="0.5" />
          <rect x="259" y="295.5" width="33" height="5" rx="1" fill={vi(0.13)} />

          <rect x="305" y="162" width="90" height="16" rx="3" fill={vi(0.17)} stroke={vi(0.28)} strokeWidth="1" />
          <text x="313" y="173.5" fontSize="7.5" fill={it} fontFamily="monospace">indexes</text>
          <rect x="305" y="178" width="90" height="10" fill={vi(0.06)} stroke={vi(0.11)} strokeWidth="0.5" />
          <rect x="309" y="180.5" width="36" height="5" rx="1" fill={vi(0.17)} />
          <rect x="305" y="188" width="90" height="10" fill={vi(0.04)} stroke={vi(0.09)} strokeWidth="0.5" />
          <rect x="309" y="190.5" width="52" height="5" rx="1" fill={vi(0.11)} />

          <rect x="155" y="157" width="90" height="16" rx="3" fill={vc(0.2)}  stroke={vc(0.32)} strokeWidth="1" />
          <text x="163" y="168.5" fontSize="7.5" fill={ct} fontFamily="monospace">columns</text>
          <rect x="155" y="173" width="90" height="10" fill={vc(0.06)} stroke={vc(0.12)} strokeWidth="0.5" />
          <rect x="159" y="175.5" width="46" height="5" rx="1" fill={vc(0.18)} />
          <rect x="155" y="183" width="90" height="10" fill={vc(0.04)} stroke={vc(0.09)} strokeWidth="0.5" />
          <rect x="159" y="185.5" width="38" height="5" rx="1" fill={vc(0.12)} />
          <rect x="155" y="193" width="90" height="10" fill={vc(0.04)} stroke={vc(0.07)} strokeWidth="0.5" />
          <rect x="159" y="195.5" width="58" height="5" rx="1" fill={vc(0.1)}  />

          <rect x="300" y="255" width="90" height="16" rx="3" fill={vc(0.16)} stroke={vc(0.26)} strokeWidth="1" />
          <text x="308" y="266.5" fontSize="7.5" fill={ct} fontFamily="monospace">constraints</text>
          <rect x="300" y="271" width="90" height="10" fill={vc(0.05)} stroke={vc(0.1)}  strokeWidth="0.5" />
          <rect x="304" y="273.5" width="42" height="5" rx="1" fill={vc(0.15)} />
          <rect x="300" y="281" width="90" height="10" fill={vc(0.04)} stroke={vc(0.08)} strokeWidth="0.5" />
          <rect x="304" y="283.5" width="30" height="5" rx="1" fill={vc(0.1)}  />

          <rect x="62"  y="257" width="93" height="16" rx="3" fill={vc(0.17)} stroke={vc(0.28)} strokeWidth="1" />
          <text x="70"  y="268.5" fontSize="7.5" fill={ct} fontFamily="monospace">relations</text>
          <rect x="62"  y="273" width="93" height="10" fill={vc(0.05)} stroke={vc(0.1)}  strokeWidth="0.5" />
          <rect x="66"  y="275.5" width="32" height="5" rx="1" fill={vc(0.16)} />
          <rect x="62"  y="283" width="93" height="10" fill={vc(0.04)} stroke={vc(0.08)} strokeWidth="0.5" />
          <rect x="66"  y="285.5" width="50" height="5" rx="1" fill={vc(0.11)} />

          <rect x="350" y="68"  width="90" height="16" rx="3" fill={vp(0.13)} stroke={vp(0.22)} strokeWidth="1" />
          <text x="358" y="79.5"  fontSize="7.5" fill={pt} fontFamily="monospace">schemas</text>
          <rect x="350" y="84"  width="90" height="10" fill={vp(0.04)} stroke={vp(0.08)} strokeWidth="0.5" />
          <rect x="354" y="86.5"  width="44" height="5" rx="1" fill={vp(0.12)} />
          <rect x="350" y="94"  width="90" height="10" fill={vp(0.03)} stroke={vp(0.07)} strokeWidth="0.5" />
          <rect x="354" y="96.5"  width="30" height="5" rx="1" fill={vp(0.09)} />

          {/* Ghost: foreign_keys */}
          <g filter="url(#auth-ghost-br)" opacity={d ? 0.36 : 0.52}>
            <rect x="152" y="385" width="96" height="16" rx="3" fill={vc(0.18)} stroke={vc(0.28)} strokeWidth="1" />
            <text x="160" y="396.5" fontSize="7.5" fill={ct} fontFamily="monospace">foreign_keys</text>
            <rect x="152" y="401" width="96" height="10" fill={vc(0.06)} stroke={vc(0.1)}  strokeWidth="0.5" />
            <rect x="156" y="403.5" width="35" height="5" rx="1" fill={vc(0.18)} />
            <rect x="152" y="411" width="96" height="10" fill={vc(0.04)} stroke={vc(0.08)} strokeWidth="0.5" />
            <rect x="156" y="413.5" width="52" height="5" rx="1" fill={vc(0.12)} />
            <rect x="152" y="421" width="96" height="10" fill={vc(0.04)} stroke={vc(0.07)} strokeWidth="0.5" />
            <rect x="156" y="423.5" width="40" height="5" rx="1" fill={vc(0.1)}  />
          </g>
        </svg>

        {/* ══════════════════════════════════════════════
            TOP-RIGHT floating ghost cluster
        ══════════════════════════════════════════════ */}
        <div style={{
          position: 'absolute', top: '4%', right: '3%',
          filter: `blur(${d ? 3.5 : 2.5}px)`,
          opacity: d ? 0.42 : 0.62,
        }}>
          <svg viewBox="0 0 310 260" width="310" height="260" overflow="visible">
            <line x1="90"  y1="32"  x2="90"  y2="90"  stroke={vp(0.3)} strokeWidth="1" className="auth-solid-pulse-3" />
            <line x1="90"  y1="90"  x2="210" y2="90"  stroke={vp(0.2)} strokeWidth="1" strokeDasharray="4,5" className="auth-line-flow-4" />
            <line x1="90"  y1="152" x2="90"  y2="200" stroke={vi(0.2)} strokeWidth="1" strokeDasharray="3,5" className="auth-line-flow-7" />
            <line x1="210" y1="32"  x2="210" y2="90"  stroke={vc(0.18)} strokeWidth="1" className="auth-solid-pulse-1" />
            <circle cx="90"  cy="32"  r="3.5" fill={vp(0.6)}  />
            <circle cx="90"  cy="90"  r="3.5" fill={vp(0.55)} />
            <circle cx="210" cy="90"  r="3"   fill={vp(0.45)} />
            <circle cx="210" cy="32"  r="3"   fill={vc(0.5)}  />
            <circle cx="90"  cy="152" r="3"   fill={vi(0.5)}  />
            {/* attributes */}
            <rect x="44"  y="15"  width="92" height="16" rx="3" fill={vp(0.18)} stroke={vp(0.3)}  strokeWidth="1" />
            <text x="52"  y="26.5"  fontSize="7.5" fill={pt} fontFamily="monospace">attributes</text>
            <rect x="44"  y="31"  width="92" height="10" fill={vp(0.06)} stroke={vp(0.10)} strokeWidth="0.5" /><rect x="48" y="33.5"  width="38" height="5" rx="1" fill={vp(0.16)} />
            <rect x="44"  y="41"  width="92" height="10" fill={vp(0.04)} stroke={vp(0.08)} strokeWidth="0.5" /><rect x="48" y="43.5"  width="55" height="5" rx="1" fill={vp(0.11)} />
            <rect x="44"  y="51"  width="92" height="10" fill={vp(0.04)} stroke={vp(0.07)} strokeWidth="0.5" /><rect x="48" y="53.5"  width="30" height="5" rx="1" fill={vp(0.11)} />
            {/* data_types */}
            <rect x="44"  y="90"  width="92" height="16" rx="3" fill={vp(0.14)} stroke={vp(0.24)} strokeWidth="1" />
            <text x="52"  y="101.5" fontSize="7.5" fill={pt} fontFamily="monospace">data_types</text>
            <rect x="44"  y="106" width="92" height="10" fill={vp(0.04)} stroke={vp(0.08)} strokeWidth="0.5" /><rect x="48" y="108.5" width="44" height="5" rx="1" fill={vp(0.13)} />
            <rect x="44"  y="116" width="92" height="10" fill={vp(0.03)} stroke={vp(0.07)} strokeWidth="0.5" /><rect x="48" y="118.5" width="32" height="5" rx="1" fill={vp(0.09)} />
            {/* enums */}
            <rect x="44"  y="152" width="92" height="16" rx="3" fill={vi(0.12)} stroke={vi(0.20)} strokeWidth="1" />
            <text x="52"  y="163.5" fontSize="7.5" fill={it} fontFamily="monospace">enums</text>
            <rect x="44"  y="168" width="92" height="10" fill={vi(0.04)} stroke={vi(0.08)} strokeWidth="0.5" /><rect x="48" y="170.5" width="36" height="5" rx="1" fill={vi(0.11)} />
            <rect x="44"  y="178" width="92" height="10" fill={vi(0.03)} stroke={vi(0.07)} strokeWidth="0.5" /><rect x="48" y="180.5" width="48" height="5" rx="1" fill={vi(0.09)} />
            {/* sequences (new, top-right) */}
            <rect x="164" y="15"  width="92" height="16" rx="3" fill={vc(0.14)} stroke={vc(0.23)} strokeWidth="1" />
            <text x="172" y="26.5"  fontSize="7.5" fill={ct} fontFamily="monospace">sequences</text>
            <rect x="164" y="31"  width="92" height="10" fill={vc(0.04)} stroke={vc(0.09)} strokeWidth="0.5" /><rect x="168" y="33.5"  width="40" height="5" rx="1" fill={vc(0.13)} />
            <rect x="164" y="41"  width="92" height="10" fill={vc(0.03)} stroke={vc(0.07)} strokeWidth="0.5" /><rect x="168" y="43.5"  width="28" height="5" rx="1" fill={vc(0.09)} />
            <rect x="164" y="51"  width="92" height="10" fill={vc(0.03)} stroke={vc(0.06)} strokeWidth="0.5" /><rect x="168" y="53.5"  width="52" height="5" rx="1" fill={vc(0.09)} />
            {/* procedures (new, below sequences) */}
            <rect x="164" y="90"  width="92" height="16" rx="3" fill={vc(0.11)} stroke={vc(0.19)} strokeWidth="1" />
            <text x="172" y="101.5" fontSize="7.5" fill={ct} fontFamily="monospace">procedures</text>
            <rect x="164" y="106" width="92" height="10" fill={vc(0.03)} stroke={vc(0.07)} strokeWidth="0.5" /><rect x="168" y="108.5" width="44" height="5" rx="1" fill={vc(0.10)} />
            <rect x="164" y="116" width="92" height="10" fill={vc(0.03)} stroke={vc(0.06)} strokeWidth="0.5" /><rect x="168" y="118.5" width="30" height="5" rx="1" fill={vc(0.07)} />
            {/* domains (new, bottom-left fading) */}
            <rect x="44"  y="200" width="92" height="16" rx="3" fill={vp(0.09)} stroke={vp(0.15)} strokeWidth="1" />
            <text x="52"  y="211.5" fontSize="7.5" fill={pt} fontFamily="monospace">domains</text>
            <rect x="44"  y="216" width="92" height="10" fill={vp(0.02)} stroke={vp(0.05)} strokeWidth="0.5" /><rect x="48" y="218.5" width="46" height="5" rx="1" fill={vp(0.08)} />
            <rect x="44"  y="226" width="92" height="10" fill={vp(0.02)} stroke={vp(0.04)} strokeWidth="0.5" /><rect x="48" y="228.5" width="32" height="5" rx="1" fill={vp(0.06)} />
          </svg>
        </div>

        {/* ══════════════════════════════════════════════
            BOTTOM-LEFT floating ghost cluster
        ══════════════════════════════════════════════ */}
        <div style={{
          position: 'absolute', bottom: '4%', left: '3%',
          filter: `blur(${d ? 3.5 : 2.5}px)`,
          opacity: d ? 0.38 : 0.55,
        }}>
          <svg viewBox="0 0 310 250" width="310" height="250" overflow="visible">
            <line x1="90"  y1="32"  x2="90"  y2="80"  stroke={vc(0.28)} strokeWidth="1" className="auth-solid-pulse-1" />
            <line x1="90"  y1="80"  x2="210" y2="80"  stroke={vc(0.20)} strokeWidth="1" strokeDasharray="4,5" className="auth-line-flow-6" />
            <line x1="90"  y1="132" x2="90"  y2="175" stroke={vi(0.20)} strokeWidth="1" strokeDasharray="3,5" className="auth-line-flow-4" />
            <line x1="210" y1="32"  x2="210" y2="80"  stroke={vp(0.18)} strokeWidth="1" className="auth-solid-pulse-2" />
            <circle cx="90"  cy="32"  r="3.5" fill={vc(0.60)} />
            <circle cx="90"  cy="80"  r="3.5" fill={vc(0.50)} />
            <circle cx="210" cy="80"  r="3"   fill={vc(0.44)} />
            <circle cx="210" cy="32"  r="3"   fill={vp(0.50)} />
            <circle cx="90"  cy="132" r="3"   fill={vi(0.48)} />
            {/* triggers */}
            <rect x="44"  y="14"  width="92" height="16" rx="3" fill={vc(0.17)} stroke={vc(0.28)} strokeWidth="1" />
            <text x="52"  y="25.5"  fontSize="7.5" fill={ct} fontFamily="monospace">triggers</text>
            <rect x="44"  y="30"  width="92" height="10" fill={vc(0.05)} stroke={vc(0.10)} strokeWidth="0.5" /><rect x="48" y="32.5"  width="36" height="5" rx="1" fill={vc(0.15)} />
            <rect x="44"  y="40"  width="92" height="10" fill={vc(0.04)} stroke={vc(0.08)} strokeWidth="0.5" /><rect x="48" y="42.5"  width="52" height="5" rx="1" fill={vc(0.10)} />
            {/* views */}
            <rect x="44"  y="80"  width="92" height="16" rx="3" fill={vc(0.14)} stroke={vc(0.22)} strokeWidth="1" />
            <text x="52"  y="91.5"  fontSize="7.5" fill={ct} fontFamily="monospace">views</text>
            <rect x="44"  y="96"  width="92" height="10" fill={vc(0.04)} stroke={vc(0.08)} strokeWidth="0.5" /><rect x="48" y="98.5"  width="42" height="5" rx="1" fill={vc(0.12)} />
            <rect x="44"  y="106" width="92" height="10" fill={vc(0.03)} stroke={vc(0.07)} strokeWidth="0.5" /><rect x="48" y="108.5" width="28" height="5" rx="1" fill={vc(0.09)} />
            {/* migrations */}
            <rect x="44"  y="132" width="92" height="16" rx="3" fill={vi(0.11)} stroke={vi(0.18)} strokeWidth="1" />
            <text x="52"  y="143.5" fontSize="7.5" fill={it} fontFamily="monospace">migrations</text>
            <rect x="44"  y="148" width="92" height="10" fill={vi(0.04)} stroke={vi(0.07)} strokeWidth="0.5" /><rect x="48" y="150.5" width="48" height="5" rx="1" fill={vi(0.10)} />
            <rect x="44"  y="158" width="92" height="10" fill={vi(0.03)} stroke={vi(0.06)} strokeWidth="0.5" /><rect x="48" y="160.5" width="32" height="5" rx="1" fill={vi(0.08)} />
            {/* extensions (new, right column, top) */}
            <rect x="164" y="14"  width="92" height="16" rx="3" fill={vp(0.13)} stroke={vp(0.22)} strokeWidth="1" />
            <text x="172" y="25.5"  fontSize="7.5" fill={pt} fontFamily="monospace">extensions</text>
            <rect x="164" y="30"  width="92" height="10" fill={vp(0.04)} stroke={vp(0.08)} strokeWidth="0.5" /><rect x="168" y="32.5"  width="44" height="5" rx="1" fill={vp(0.12)} />
            <rect x="164" y="40"  width="92" height="10" fill={vp(0.03)} stroke={vp(0.07)} strokeWidth="0.5" /><rect x="168" y="42.5"  width="30" height="5" rx="1" fill={vp(0.09)} />
            <rect x="164" y="50"  width="92" height="10" fill={vp(0.03)} stroke={vp(0.06)} strokeWidth="0.5" /><rect x="168" y="52.5"  width="56" height="5" rx="1" fill={vp(0.09)} />
            {/* functions (new, right column, mid) */}
            <rect x="164" y="80"  width="92" height="16" rx="3" fill={vp(0.11)} stroke={vp(0.18)} strokeWidth="1" />
            <text x="172" y="91.5"  fontSize="7.5" fill={pt} fontFamily="monospace">functions</text>
            <rect x="164" y="96"  width="92" height="10" fill={vp(0.03)} stroke={vp(0.07)} strokeWidth="0.5" /><rect x="168" y="98.5"  width="36" height="5" rx="1" fill={vp(0.10)} />
            <rect x="164" y="106" width="92" height="10" fill={vp(0.03)} stroke={vp(0.06)} strokeWidth="0.5" /><rect x="168" y="108.5" width="52" height="5" rx="1" fill={vp(0.08)} />
            {/* packages (fading, bottom-right) */}
            <rect x="164" y="175" width="92" height="16" rx="3" fill={vi(0.09)} stroke={vi(0.15)} strokeWidth="1" />
            <text x="172" y="186.5" fontSize="7.5" fill={it} fontFamily="monospace">packages</text>
            <rect x="164" y="191" width="92" height="10" fill={vi(0.02)} stroke={vi(0.05)} strokeWidth="0.5" /><rect x="168" y="193.5" width="38" height="5" rx="1" fill={vi(0.07)} />
            {/* seed_data (fading, bottom) */}
            <rect x="44"  y="175" width="92" height="16" rx="3" fill={vp(0.08)} stroke={vp(0.14)} strokeWidth="1" />
            <text x="52"  y="186.5" fontSize="7.5" fill={pt} fontFamily="monospace">seed_data</text>
            <rect x="44"  y="191" width="92" height="10" fill={vp(0.02)} stroke={vp(0.05)} strokeWidth="0.5" /><rect x="48" y="193.5" width="44" height="5" rx="1" fill={vp(0.07)} />
          </svg>
        </div>

        {/* ══════════════════════════════════════════════
            TOP-CENTER ghost cluster (api_keys, sessions, tokens)
        ══════════════════════════════════════════════ */}
        <div style={{
          position: 'absolute', top: '2%', left: '36%',
          filter: `blur(${d ? 6 : 4.5}px)`,
          opacity: d ? 0.30 : 0.44,
        }}>
          <svg viewBox="0 0 270 155" width="270" height="155" overflow="visible">
            <line x1="94"  y1="62" x2="94"  y2="80" stroke={vi(0.22)} strokeWidth="1" strokeDasharray="3,5" className="auth-line-flow-3" />
            <line x1="130" y1="32" x2="175" y2="32" stroke={vc(0.18)} strokeWidth="1" strokeDasharray="4,5" className="auth-line-flow-6" />
            <circle cx="94"  cy="62" r="2.5" fill={vi(0.55)} />
            <circle cx="94"  cy="80" r="2.5" fill={vi(0.5)}  />
            <circle cx="130" cy="32" r="2.5" fill={vc(0.5)}  />
            <circle cx="175" cy="32" r="2.5" fill={vc(0.44)} />
            {/* api_keys */}
            <rect x="46" y="10" width="96" height="16" rx="3" fill={vi(0.15)} stroke={vi(0.24)} strokeWidth="1" />
            <text x="54" y="21.5" fontSize="7.5" fill={it} fontFamily="monospace">api_keys</text>
            <rect x="46" y="26" width="96" height="10" fill={vi(0.05)} stroke={vi(0.09)} strokeWidth="0.5" /><rect x="50" y="28.5" width="34" height="5" rx="1" fill={vi(0.14)} />
            <rect x="46" y="36" width="96" height="10" fill={vi(0.03)} stroke={vi(0.07)} strokeWidth="0.5" /><rect x="50" y="38.5" width="52" height="5" rx="1" fill={vi(0.09)} />
            <rect x="46" y="46" width="96" height="10" fill={vi(0.03)} stroke={vi(0.06)} strokeWidth="0.5" /><rect x="50" y="48.5" width="38" height="5" rx="1" fill={vi(0.09)} />
            {/* sessions */}
            <rect x="46" y="80" width="96" height="16" rx="3" fill={vc(0.13)} stroke={vc(0.21)} strokeWidth="1" />
            <text x="54" y="91.5" fontSize="7.5" fill={ct} fontFamily="monospace">sessions</text>
            <rect x="46" y="96" width="96" height="10" fill={vc(0.04)} stroke={vc(0.08)} strokeWidth="0.5" /><rect x="50" y="98.5" width="44" height="5" rx="1" fill={vc(0.12)} />
            <rect x="46" y="106" width="96" height="10" fill={vc(0.03)} stroke={vc(0.06)} strokeWidth="0.5" /><rect x="50" y="108.5" width="30" height="5" rx="1" fill={vc(0.08)} />
            <rect x="46" y="116" width="96" height="10" fill={vc(0.03)} stroke={vc(0.06)} strokeWidth="0.5" /><rect x="50" y="118.5" width="58" height="5" rx="1" fill={vc(0.08)} />
            {/* tokens (to the right) */}
            <rect x="175" y="10" width="88" height="16" rx="3" fill={vc(0.11)} stroke={vc(0.18)} strokeWidth="1" />
            <text x="183" y="21.5" fontSize="7.5" fill={ct} fontFamily="monospace">tokens</text>
            <rect x="175" y="26" width="88" height="10" fill={vc(0.03)} stroke={vc(0.07)} strokeWidth="0.5" /><rect x="179" y="28.5" width="36" height="5" rx="1" fill={vc(0.10)} />
            <rect x="175" y="36" width="88" height="10" fill={vc(0.03)} stroke={vc(0.06)} strokeWidth="0.5" /><rect x="179" y="38.5" width="50" height="5" rx="1" fill={vc(0.07)} />
          </svg>
        </div>

        {/* ══════════════════════════════════════════════
            BOTTOM-CENTER ghost cluster (files, storage, media)
        ══════════════════════════════════════════════ */}
        <div style={{
          position: 'absolute', bottom: '2%', left: '28%',
          filter: `blur(${d ? 7 : 5}px)`,
          opacity: d ? 0.26 : 0.40,
        }}>
          <svg viewBox="0 0 340 80" width="340" height="80" overflow="visible">
            <line x1="115" y1="32" x2="160" y2="32" stroke={vc(0.2)} strokeWidth="1" strokeDasharray="4,5" className="auth-line-flow-5" />
            <line x1="250" y1="32" x2="265" y2="32" stroke={vp(0.17)} strokeWidth="1" strokeDasharray="3,6" className="auth-line-flow-7" />
            <circle cx="115" cy="32" r="2.5" fill={vc(0.5)}  />
            <circle cx="160" cy="32" r="2.5" fill={vc(0.44)} />
            <circle cx="250" cy="32" r="2.5" fill={vp(0.44)} />
            {/* files */}
            <rect x="20"  y="10" width="95" height="16" rx="3" fill={vc(0.14)} stroke={vc(0.23)} strokeWidth="1" />
            <text x="28"  y="21.5" fontSize="7.5" fill={ct} fontFamily="monospace">files</text>
            <rect x="20"  y="26" width="95" height="10" fill={vc(0.04)} stroke={vc(0.08)} strokeWidth="0.5" /><rect x="24" y="28.5" width="40" height="5" rx="1" fill={vc(0.12)} />
            <rect x="20"  y="36" width="95" height="10" fill={vc(0.03)} stroke={vc(0.07)} strokeWidth="0.5" /><rect x="24" y="38.5" width="58" height="5" rx="1" fill={vc(0.09)} />
            <rect x="20"  y="46" width="95" height="10" fill={vc(0.03)} stroke={vc(0.06)} strokeWidth="0.5" /><rect x="24" y="48.5" width="30" height="5" rx="1" fill={vc(0.09)} />
            {/* storage */}
            <rect x="160" y="10" width="90" height="16" rx="3" fill={vi(0.12)} stroke={vi(0.20)} strokeWidth="1" />
            <text x="168" y="21.5" fontSize="7.5" fill={it} fontFamily="monospace">storage</text>
            <rect x="160" y="26" width="90" height="10" fill={vi(0.04)} stroke={vi(0.07)} strokeWidth="0.5" /><rect x="164" y="28.5" width="46" height="5" rx="1" fill={vi(0.11)} />
            <rect x="160" y="36" width="90" height="10" fill={vi(0.03)} stroke={vi(0.06)} strokeWidth="0.5" /><rect x="164" y="38.5" width="28" height="5" rx="1" fill={vi(0.08)} />
            {/* media */}
            <rect x="265" y="10" width="90" height="16" rx="3" fill={vp(0.11)} stroke={vp(0.19)} strokeWidth="1" />
            <text x="273" y="21.5" fontSize="7.5" fill={pt} fontFamily="monospace">media</text>
            <rect x="265" y="26" width="90" height="10" fill={vp(0.03)} stroke={vp(0.07)} strokeWidth="0.5" /><rect x="269" y="28.5" width="38" height="5" rx="1" fill={vp(0.09)} />
            <rect x="265" y="36" width="90" height="10" fill={vp(0.03)} stroke={vp(0.06)} strokeWidth="0.5" /><rect x="269" y="38.5" width="52" height="5" rx="1" fill={vp(0.07)} />
            <rect x="265" y="46" width="90" height="10" fill={vp(0.02)} stroke={vp(0.05)} strokeWidth="0.5" /><rect x="269" y="48.5" width="26" height="5" rx="1" fill={vp(0.07)} />
          </svg>
        </div>

        {/* ══════════════════════════════════════════════
            LEFT-MID ghost cluster (notifications, events)
        ══════════════════════════════════════════════ */}
        <div style={{
          position: 'absolute', top: '36%', left: '1%',
          filter: `blur(${d ? 11 : 8}px)`,
          opacity: d ? 0.22 : 0.34,
        }}>
          <svg viewBox="0 0 210 205" width="210" height="205" overflow="visible">
            <line x1="100" y1="66" x2="100" y2="96" stroke={vp(0.22)} strokeWidth="1" className="auth-solid-pulse-2" />
            <line x1="100" y1="152" x2="160" y2="152" stroke={vc(0.16)} strokeWidth="1" strokeDasharray="3,5" className="auth-line-flow-5" />
            <circle cx="100" cy="66"  r="2.5" fill={vp(0.5)}  />
            <circle cx="100" cy="96"  r="2.5" fill={vp(0.44)} />
            <circle cx="100" cy="152" r="2.5" fill={vc(0.44)} />
            {/* notifications */}
            <rect x="52" y="10" width="98" height="16" rx="3" fill={vp(0.13)} stroke={vp(0.21)} strokeWidth="1" />
            <text x="60" y="21.5" fontSize="7.5" fill={pt} fontFamily="monospace">notifications</text>
            <rect x="52" y="26" width="98" height="10" fill={vp(0.04)} stroke={vp(0.08)} strokeWidth="0.5" /><rect x="56" y="28.5" width="36" height="5" rx="1" fill={vp(0.12)} />
            <rect x="52" y="36" width="98" height="10" fill={vp(0.03)} stroke={vp(0.07)} strokeWidth="0.5" /><rect x="56" y="38.5" width="55" height="5" rx="1" fill={vp(0.09)} />
            <rect x="52" y="46" width="98" height="10" fill={vp(0.03)} stroke={vp(0.06)} strokeWidth="0.5" /><rect x="56" y="48.5" width="40" height="5" rx="1" fill={vp(0.09)} />
            <rect x="52" y="56" width="98" height="10" fill={vp(0.02)} stroke={vp(0.05)} strokeWidth="0.5" /><rect x="56" y="58.5" width="28" height="5" rx="1" fill={vp(0.07)} />
            {/* events */}
            <rect x="52" y="96" width="98" height="16" rx="3" fill={vc(0.11)} stroke={vc(0.19)} strokeWidth="1" />
            <text x="60" y="107.5" fontSize="7.5" fill={ct} fontFamily="monospace">events</text>
            <rect x="52" y="112" width="98" height="10" fill={vc(0.03)} stroke={vc(0.07)} strokeWidth="0.5" /><rect x="56" y="114.5" width="44" height="5" rx="1" fill={vc(0.10)} />
            <rect x="52" y="122" width="98" height="10" fill={vc(0.03)} stroke={vc(0.06)} strokeWidth="0.5" /><rect x="56" y="124.5" width="32" height="5" rx="1" fill={vc(0.08)} />
            <rect x="52" y="132" width="98" height="10" fill={vc(0.02)} stroke={vc(0.05)} strokeWidth="0.5" /><rect x="56" y="134.5" width="60" height="5" rx="1" fill={vc(0.08)} />
            {/* webhooks (smaller, to the right of events line) */}
            <rect x="52" y="152" width="98" height="16" rx="3" fill={vi(0.10)} stroke={vi(0.17)} strokeWidth="1" />
            <text x="60" y="163.5" fontSize="7.5" fill={it} fontFamily="monospace">webhooks</text>
            <rect x="52" y="168" width="98" height="10" fill={vi(0.03)} stroke={vi(0.06)} strokeWidth="0.5" /><rect x="56" y="170.5" width="38" height="5" rx="1" fill={vi(0.09)} />
            <rect x="52" y="178" width="98" height="10" fill={vi(0.02)} stroke={vi(0.05)} strokeWidth="0.5" /><rect x="56" y="180.5" width="50" height="5" rx="1" fill={vi(0.07)} />
          </svg>
        </div>

        {/* ══════════════════════════════════════════════
            RIGHT-MID ghost cluster (audit_logs, activity, tags)
        ══════════════════════════════════════════════ */}
        <div style={{
          position: 'absolute', top: '26%', right: '1%',
          filter: `blur(${d ? 10 : 7}px)`,
          opacity: d ? 0.23 : 0.36,
        }}>
          <svg viewBox="0 0 210 210" width="210" height="210" overflow="visible">
            <line x1="100" y1="62" x2="100" y2="96" stroke={vi(0.2)} strokeWidth="1" strokeDasharray="3,5" className="auth-line-flow-6" />
            <line x1="100" y1="152" x2="100" y2="170" stroke={vp(0.16)} strokeWidth="1" strokeDasharray="2,6" className="auth-line-flow-2" />
            <circle cx="100" cy="62"  r="2.5" fill={vi(0.5)}  />
            <circle cx="100" cy="96"  r="2.5" fill={vi(0.44)} />
            <circle cx="100" cy="152" r="2.5" fill={vp(0.44)} />
            {/* audit_logs */}
            <rect x="52" y="10" width="98" height="16" rx="3" fill={vi(0.12)} stroke={vi(0.20)} strokeWidth="1" />
            <text x="60" y="21.5" fontSize="7.5" fill={it} fontFamily="monospace">audit_logs</text>
            <rect x="52" y="26" width="98" height="10" fill={vi(0.04)} stroke={vi(0.08)} strokeWidth="0.5" /><rect x="56" y="28.5" width="34" height="5" rx="1" fill={vi(0.11)} />
            <rect x="52" y="36" width="98" height="10" fill={vi(0.03)} stroke={vi(0.07)} strokeWidth="0.5" /><rect x="56" y="38.5" width="52" height="5" rx="1" fill={vi(0.08)} />
            <rect x="52" y="46" width="98" height="10" fill={vi(0.03)} stroke={vi(0.06)} strokeWidth="0.5" /><rect x="56" y="48.5" width="38" height="5" rx="1" fill={vi(0.08)} />
            <rect x="52" y="56" width="98" height="10" fill={vi(0.02)} stroke={vi(0.05)} strokeWidth="0.5" /><rect x="56" y="58.5" width="28" height="5" rx="1" fill={vi(0.06)} />
            {/* activity */}
            <rect x="52" y="96" width="98" height="16" rx="3" fill={vp(0.10)} stroke={vp(0.17)} strokeWidth="1" />
            <text x="60" y="107.5" fontSize="7.5" fill={pt} fontFamily="monospace">activity</text>
            <rect x="52" y="112" width="98" height="10" fill={vp(0.03)} stroke={vp(0.07)} strokeWidth="0.5" /><rect x="56" y="114.5" width="44" height="5" rx="1" fill={vp(0.09)} />
            <rect x="52" y="122" width="98" height="10" fill={vp(0.03)} stroke={vp(0.06)} strokeWidth="0.5" /><rect x="56" y="124.5" width="30" height="5" rx="1" fill={vp(0.07)} />
            <rect x="52" y="132" width="98" height="10" fill={vp(0.02)} stroke={vp(0.05)} strokeWidth="0.5" /><rect x="56" y="134.5" width="55" height="5" rx="1" fill={vp(0.07)} />
            {/* tags */}
            <rect x="52" y="170" width="98" height="16" rx="3" fill={vc(0.09)} stroke={vc(0.16)} strokeWidth="1" />
            <text x="60" y="181.5" fontSize="7.5" fill={ct} fontFamily="monospace">tags</text>
            <rect x="52" y="186" width="98" height="10" fill={vc(0.03)} stroke={vc(0.06)} strokeWidth="0.5" /><rect x="56" y="188.5" width="40" height="5" rx="1" fill={vc(0.08)} />
            <rect x="52" y="196" width="98" height="10" fill={vc(0.02)} stroke={vc(0.05)} strokeWidth="0.5" /><rect x="56" y="198.5" width="28" height="5" rx="1" fill={vc(0.06)} />
          </svg>
        </div>

        {/* ══════════════════════════════════════════════
            DEEP BACKGROUND — center-left (tokens, cache, queue_jobs)
            Extreme blur — only shapes visible
        ══════════════════════════════════════════════ */}
        <div style={{
          position: 'absolute', top: '16%', left: '20%',
          filter: `blur(${d ? 17 : 13}px)`,
          opacity: d ? 0.13 : 0.20,
        }}>
          <svg viewBox="0 0 250 230" width="250" height="230" overflow="visible">
            <line x1="125" y1="66" x2="125" y2="96"  stroke={vc(0.18)} strokeWidth="1" />
            <line x1="125" y1="152" x2="125" y2="186" stroke={vp(0.14)} strokeWidth="1" strokeDasharray="3,5" />
            {/* tokens */}
            <rect x="78" y="10" width="96" height="16" rx="3" fill={vc(0.11)} stroke={vc(0.18)} strokeWidth="1" />
            <text x="86" y="21.5" fontSize="7.5" fill={ct} fontFamily="monospace">tokens</text>
            <rect x="78" y="26" width="96" height="10" fill={vc(0.04)} stroke={vc(0.07)} strokeWidth="0.5" /><rect x="82" y="28.5" width="44" height="5" rx="1" fill={vc(0.10)} />
            <rect x="78" y="36" width="96" height="10" fill={vc(0.03)} stroke={vc(0.06)} strokeWidth="0.5" /><rect x="82" y="38.5" width="30" height="5" rx="1" fill={vc(0.07)} />
            <rect x="78" y="46" width="96" height="10" fill={vc(0.03)} stroke={vc(0.05)} strokeWidth="0.5" /><rect x="82" y="48.5" width="55" height="5" rx="1" fill={vc(0.07)} />
            <rect x="78" y="56" width="96" height="10" fill={vc(0.02)} stroke={vc(0.04)} strokeWidth="0.5" /><rect x="82" y="58.5" width="35" height="5" rx="1" fill={vc(0.06)} />
            {/* cache */}
            <rect x="78" y="96" width="96" height="16" rx="3" fill={vi(0.09)} stroke={vi(0.16)} strokeWidth="1" />
            <text x="86" y="107.5" fontSize="7.5" fill={it} fontFamily="monospace">cache</text>
            <rect x="78" y="112" width="96" height="10" fill={vi(0.03)} stroke={vi(0.06)} strokeWidth="0.5" /><rect x="82" y="114.5" width="38" height="5" rx="1" fill={vi(0.08)} />
            <rect x="78" y="122" width="96" height="10" fill={vi(0.03)} stroke={vi(0.05)} strokeWidth="0.5" /><rect x="82" y="124.5" width="52" height="5" rx="1" fill={vi(0.06)} />
            <rect x="78" y="132" width="96" height="10" fill={vi(0.02)} stroke={vi(0.04)} strokeWidth="0.5" /><rect x="82" y="134.5" width="25" height="5" rx="1" fill={vi(0.06)} />
            {/* queue_jobs */}
            <rect x="78" y="186" width="96" height="16" rx="3" fill={vp(0.08)} stroke={vp(0.14)} strokeWidth="1" />
            <text x="86" y="197.5" fontSize="7.5" fill={pt} fontFamily="monospace">queue_jobs</text>
            <rect x="78" y="202" width="96" height="10" fill={vp(0.02)} stroke={vp(0.05)} strokeWidth="0.5" /><rect x="82" y="204.5" width="46" height="5" rx="1" fill={vp(0.07)} />
            <rect x="78" y="212" width="96" height="10" fill={vp(0.02)} stroke={vp(0.04)} strokeWidth="0.5" /><rect x="82" y="214.5" width="32" height="5" rx="1" fill={vp(0.06)} />
          </svg>
        </div>

        {/* ══════════════════════════════════════════════
            DEEP BACKGROUND — center-right (settings, configs, feature_flags)
            Extreme blur — only shapes visible
        ══════════════════════════════════════════════ */}
        <div style={{
          position: 'absolute', top: '44%', right: '14%',
          filter: `blur(${d ? 16 : 12}px)`,
          opacity: d ? 0.14 : 0.21,
        }}>
          <svg viewBox="0 0 240 210" width="240" height="210" overflow="visible">
            <line x1="120" y1="62" x2="120" y2="96"  stroke={vp(0.16)} strokeWidth="1" strokeDasharray="3,5" />
            <line x1="120" y1="148" x2="120" y2="180" stroke={vi(0.13)} strokeWidth="1" />
            {/* settings */}
            <rect x="72" y="10" width="96" height="16" rx="3" fill={vp(0.09)} stroke={vp(0.15)} strokeWidth="1" />
            <text x="80" y="21.5" fontSize="7.5" fill={pt} fontFamily="monospace">settings</text>
            <rect x="72" y="26" width="96" height="10" fill={vp(0.03)} stroke={vp(0.06)} strokeWidth="0.5" /><rect x="76" y="28.5" width="36" height="5" rx="1" fill={vp(0.08)} />
            <rect x="72" y="36" width="96" height="10" fill={vp(0.03)} stroke={vp(0.05)} strokeWidth="0.5" /><rect x="76" y="38.5" width="50" height="5" rx="1" fill={vp(0.06)} />
            <rect x="72" y="46" width="96" height="10" fill={vp(0.02)} stroke={vp(0.04)} strokeWidth="0.5" /><rect x="76" y="48.5" width="30" height="5" rx="1" fill={vp(0.06)} />
            <rect x="72" y="56" width="96" height="10" fill={vp(0.02)} stroke={vp(0.04)} strokeWidth="0.5" /><rect x="76" y="58.5" width="62" height="5" rx="1" fill={vp(0.05)} />
            {/* configs */}
            <rect x="72" y="96" width="96" height="16" rx="3" fill={vc(0.08)} stroke={vc(0.15)} strokeWidth="1" />
            <text x="80" y="107.5" fontSize="7.5" fill={ct} fontFamily="monospace">configs</text>
            <rect x="72" y="112" width="96" height="10" fill={vc(0.03)} stroke={vc(0.06)} strokeWidth="0.5" /><rect x="76" y="114.5" width="42" height="5" rx="1" fill={vc(0.07)} />
            <rect x="72" y="122" width="96" height="10" fill={vc(0.02)} stroke={vc(0.05)} strokeWidth="0.5" /><rect x="76" y="124.5" width="28" height="5" rx="1" fill={vc(0.05)} />
            <rect x="72" y="132" width="96" height="10" fill={vc(0.02)} stroke={vc(0.04)} strokeWidth="0.5" /><rect x="76" y="134.5" width="55" height="5" rx="1" fill={vc(0.05)} />
            {/* feature_flags */}
            <rect x="72" y="180" width="96" height="16" rx="3" fill={vi(0.07)} stroke={vi(0.13)} strokeWidth="1" />
            <text x="80" y="191.5" fontSize="7.5" fill={it} fontFamily="monospace">feature_flags</text>
            <rect x="72" y="196" width="96" height="10" fill={vi(0.02)} stroke={vi(0.05)} strokeWidth="0.5" /><rect x="76" y="198.5" width="38" height="5" rx="1" fill={vi(0.06)} />
          </svg>
        </div>

        {/* ── Floating particles ── */}
        {[
          { top:'18%', left:'12%',  size:3,   color: vi(0.75), glow: vi(0.55), cls:'auth-particle-1' },
          { top:'66%', right:'14%', size:2.5, color: vc(0.65), glow: vc(0.45), cls:'auth-particle-2' },
          { top:'36%', right:'9%',  size:3,   color: vp(0.65), glow: vp(0.45), cls:'auth-particle-3' },
          { bottom:'24%', left:'7%',size:2,   color: vi(0.75), glow: vi(0.55), cls:'auth-particle-4' },
          { top:'52%', left:'28%',  size:2,   color: vc(0.55), glow:'',        cls:'auth-particle-5' },
          { top:'28%', right:'28%', size:2,   color: vi(0.55), glow:'',        cls:'auth-particle-6' },
          { bottom:'35%', right:'38%', size:1.5, color: vp(0.5), glow:'',     cls:'auth-particle-7' },
          { top:'44%', left:'5%',   size:2,   color: vp(0.5), glow:'',         cls:'auth-particle-1' },
          { top:'8%',  right:'22%', size:2,   color: vc(0.55), glow:'',        cls:'auth-particle-2' },
          { bottom:'12%', left:'22%', size:2.5, color: vi(0.6), glow: vi(0.4), cls:'auth-particle-3' },
        ].map((p, idx) => (
          <div
            key={idx}
            className={p.cls}
            style={{
              position: 'absolute',
              ...(p.top    ? { top:    p.top    } : {}),
              ...(p.bottom ? { bottom: p.bottom } : {}),
              ...(p.left   ? { left:   p.left   } : {}),
              ...(p.right  ? { right:  p.right  } : {}),
              width:  p.size,
              height: p.size,
              borderRadius: '50%',
              background: p.color,
              boxShadow: p.glow ? `0 0 7px ${p.glow}` : undefined,
            }}
          />
        ))}
      </div>

      {/* ── Content slot ── */}
      <div style={{
        position: 'relative',
        zIndex: 10,
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        padding: 24,
      }}>
        {children}
      </div>
    </div>
  )
}
